import React, { Component } from 'react';
import { includes, values } from 'lodash';
import { Field } from 'redux-form';
import FormField from '../form-field';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import getCardName from '../../lib/card-type-names';
import Purchase from '../../lib/purchase';
import {
  normalizeExpirationDate,
  isNumberChangedEvent,
  isCvvChangedEvent,
} from '../../lib/card-functions';
import { formatMessage } from '../../lib/intl';
import { ZIP_CODE_FIELD } from '../../lib/card-field-names';

import './../../css/card-fields.css';

const Spreedly = window.Spreedly;

const i18n = defineMessages({
  cardError: {
    id: 'cardFields.errors.cardError',
    defaultMessage: 'please review your card information',
  },
  cardVerificationFail: {
    id: 'cardFields.errors.cardVerificationFail',
    defaultMessage: `Card verification failed - check your card info,
      contact your bank to approve the payment, or use a new card`,
  },
  expDateFieldPlaceholder: {
    id: 'cardFields.expDateField.placeholder',
    defaultMessage: 'mm/yy',
  },
  zipCodeFieldPlaceholder: {
    id: 'cardFields.zipCodeField.placeholder',
    defaultMessage: 'Postal code',
  },
});

class CardFields extends Component {
  constructor(props) {
    super(props);
    this.state = {
      validCC: true,
      cardAccepted: true,
      cardType: '',
      isNumberEmpty: true,
      isCvvEmpty: true,
      isCvvValid: false,
    };

    this.formatMessage = formatMessage(this.props.intl);
  }

  componentDidMount() {
    this.props.initializer({
      config: this.props.config,
      onRemoteFieldChange: this.onRemoteFieldChange,
      onSuccess: this.onSuccess,
      onError: this.onError,
    });
  }

  componentWillUnmount() {
    if (!Spreedly) return;

    Spreedly.unload();
    Spreedly.removeHandlers();
  }

  onSuccess = (token) => {
    return this.props.cardTokenized(token);
  }

  onError = (error) => {
    return this.props.onCardError(this.formatMessage(i18n.cardError));
  }

  isCardTypeValid(cardType) {
    return cardType && includes(Object.keys(this.validCardTypes), cardType);
  }

  onCardTypeRecognized(cardType) {
    if (cardType && includes(Object.keys(this.validCardTypes), cardType)) {
      this.setState({ cardAccepted: true });
    }

    this.showCardType(cardType);
    this.setCardType(cardType);
  }

  onCardTypeNotRecognized() {
    this.showCardType('generic');
    this.setCardType('');
  }

  onRemoteFieldChange = (name, evType, activeEl, data) => {
    const id = name === 'number' ? 'card-field' : 'cvv-field';
    const el = document.getElementById(id);

    if (evType === 'focus') return el.classList.add('spreedly-input-focus');
    if (isNumberChangedEvent(evType, name)) return this.handleNumberChanged(data);
    if (isCvvChangedEvent(evType, name)) return this.onCvvChanged(data);
    if (evType === 'blur') return this.onBlur(el, name, data);

    return null;
  }

  onBlur(el, name, data) {
    el.classList.remove('spreedly-input-focus');
    if (name === 'cvv') {
      this.validateCvvValidity();
    }

    if (name === 'number') {
      if (this.isUnsupportedCard()) {
        this.onInvalidCreditCard();
      } else if (this.state.validCC === false || this.state.cardAccepted === false) {
        addErrorClassTo('card-field');
      } else {
        this.setCardAsValid();
      }
    }
  }

  onCvvChanged(data) {
    this.setState({
      isCvvEmpty: !data.cvvLength,
      isCvvValid: data.validCvv,
    });
    this.props.onCardFieldsTouched && this.props.onCardFieldsTouched();
    this.validateCvvValidity();
  }

  handleNumberChanged(data) {
    if (data.cardType) {
      this.onCardTypeRecognized(data.cardType);
      this.props.onNumberChange && this.props.onNumberChange();
    } else {
      this.onCardTypeNotRecognized();
    }

    this.setState({ validCC: data.validNumber });
    this.props.onCardFieldsTouched && this.props.onCardFieldsTouched();

    removeErrorClassFrom('card-field');
  }

  onInvalidCreditCard() {
    this.setState({ cardAccepted: false });
    addErrorClassTo('card-field');
    this.props.onInvalidCard && this.props.onInvalidCard();
  }

  setCardType(cardType) {
    this.setState({ cardType });
  }

  setCardAsValid() {
    this.setState({ validCC: true });
    this.props.onValidCard && this.props.onValidCard();
    removeErrorClassFrom('card-field');
  }

  showCardType(type) {
    const el = document.getElementById('card-field');
    const classNames = [
      'cc-icon-amex', 'cc-icon-diners-club', 'cc-icon-discover',
      'cc-icon-mc', 'cc-icon-master', 'cc-icon-master-card',
      'cc-icon-visa', 'cc-icon-generic',
    ];
    el.classList.remove(...classNames);
    el.classList.add(`cc-icon-${this.validCardTypes[type]}`);
  }

  isCvvValid() { return !this.state.isCvvEmpty && this.state.isCvvValid; }

  isNumberEmpty() { return this.state.isNumberEmpty; }

  isFieldDisabled(fieldName) { return this.props.disabledFields.indexOf(fieldName) > -1; }

  validatePresenceOfNumber() {
    if (this.isNumberEmpty()) return addErrorClassTo('card-field');
    return removeErrorClassFrom('card-field');
  }

  validateCvvValidity() {
    if (!this.isCvvValid()) return addErrorClassTo('cvv-field');
    return removeErrorClassFrom('cvv-field');
  }

  isUnsupportedCard() {
    if (!this.state.cardType || this.state.cardType === '') return false;
    return !includes(Object.keys(this.validCardTypes), this.state.cardType);
  }

  get validCardTypes() {
    return Purchase.getValidCardTypes(this.props.currencyConfig);
  }

  isAccepted() { return this.state.cardAccepted; }

  renderCreditCardError() {
    if (this.isAccepted()) return null;
    const { currencyCode } = this.props;
    const creditCard = getCardName(this.state.cardType);

    return (
      <span className="small-print">
        <FormattedMessage
          id="cardFields.errors.cardNotAcceptedForCurrency"
          defaultMessage="{creditCard} is not accepted for {currency}"
          values={{ creditCard, currency: currencyCode.toUpperCase() }}
        />
      </span>
    );
  }

  renderCreditCardIcons() {
    return (
      <span className="card-icons pull-right">
        {
          values(this.validCardTypes).map(cc => (
            <span className={`icon ${cc}`} key={cc} />
          ))
        }
      </span>
    );
  }

  render() {
    return (
      <div className="card-fields">
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              {this.renderCreditCardIcons()}
              <label>
                <FormattedMessage
                  id="cardFields.cardNumberField.label"
                  defaultMessage="Card number"
                />
              </label>
              <div
                className="form-control cc-field cc-icon-generic"
                style={{ height: '38px' }}
                id="card-field"
                name="cardNumber"
              />
              {this.renderCreditCardError()}
            </div>
          </div>
          <div className="col-3">
            <Field
              name="expDate"
              type="text"
              placeholder={this.formatMessage.expDateFieldPlaceholder}
              format={normalizeExpirationDate}
              component={FormField}
              onChange={this.props.onCardFieldsTouched}
              label={<FormattedMessage
                id="cardFields.expDateField.label"
                defaultMessage="Expiration date"
              />}
            />
          </div>
          <div className="col-3">
            <div className="form-group">
              <label>
                <FormattedMessage
                  id="cardFields.securityCodeField.label"
                  defaultMessage="Security code"
                />
              </label>
              <div
                className="form-control"
                style={{ height: '38px' }}
                id="cvv-field"
                name="cvv"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <Field
              name="fullName"
              type="text"
              component={FormField}
              onChange={this.props.onCardFieldsTouched}
              label={
                <FormattedMessage
                  id="cardFields.fullNameField.label"
                  defaultMessage="Full name"
                />
              }
            />
          </div>
          <div className="col-6">
            {!this.isFieldDisabled(ZIP_CODE_FIELD) && (
              <Field
                name="zipCode"
                type="text"
                placeholder={this.formatMessage(i18n.zipCodeFieldPlaceholder)}
                component={FormField}
                label={<FormattedMessage
                  id="cardFields.zipCodeField.label"
                  defaultMessage="Billing postal code"
                />}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

CardFields.defaultProps = {
  spreedlyKey: '',
  currencyCode: 'USD',
  disabledFields: [],
  onInvalidCard: () => { },
  onValidCard: () => { },
};

export default injectIntl(CardFields);

const addErrorClassTo = (id) => {
  const el = document.getElementById(id);
  el.classList.add('field-with-error');
};

const removeErrorClassFrom = (id) => {
  const el = document.getElementById(id);
  el.classList.remove('field-with-error');
};



// WEBPACK FOOTER //
// ./src/desktop/components/commit-form/card-fields.js